import type { Address, Party } from '@my/directory-api';
import { useLocations, useParties, useSitesWithMembers } from '@my/directory-api/hooks';
import { tmsApi } from '@my/tms-api';
import type { CargoDefinition, OrderTask, OrderTaskResource, ServiceTag } from '@my/tms-api';
import {
  orderTaskKeys,
  useCargoDefinitions,
  useLoads,
  useOrganisationResources,
  useOrders,
  useOrdersEmissions,
  useServiceTags,
} from '@my/tms-api/hooks';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  buildMovements,
  cargoTypeOptions,
  directionOptions,
  isOnPlan,
  type PlanMovement,
} from '../lib/movements';

/**
 * Assembles the live Job Queue: un-manifested full-load orders + built loads,
 * hydrated with customer / cargo / journey / window from the TMS + Directory
 * APIs. Same data backbone the Manifest screen uses.
 */
export function usePlanMovements(
  organisationId: string | null,
  /** Selected depot — its country anchors the Direction grouping. */
  depotSiteId: string | null = null,
  /** Include Completed jobs (off by default — Plan is about the open work). */
  showCompleted = false,
): {
  movements: PlanMovement[];
  /** ISO country of the selected depot, or null when unknown. */
  depotCountry: string | null;
  /** Cargo-type filter options — the organisation's own cargo definitions. */
  cargoOptions: string[];
  /** Direction filter options — the country pairs present in the current queue. */
  directionOptions: string[];
  /**
   * Every assignment in the organisation, flat and task-scoped. Surfaced so the
   * allocation panel can show per-stop crew — a load's inline `resources` are
   * deduped across tasks and drop the task id, so they can't answer that.
   */
  assignments: OrderTaskResource[];
  /**
   * Order id → order number. Needed by the allocation panel: a load's stops can
   * span several orders, so each stop has to say which one it belongs to.
   */
  orderNumberById: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
} {
  const ordersQuery = useOrders(organisationId);
  const loadsQuery = useLoads(organisationId);
  // One org-wide request rather than a per-order fan-out: the queue lists every
  // order, so fetching assignments row-by-row would multiply the page's requests.
  const assignmentsQuery = useOrganisationResources(organisationId);
  const customersQuery = useParties(organisationId, 'Customer');
  const locationsQuery = useLocations(organisationId);
  const serviceTagsQuery = useServiceTags(organisationId);
  const cargoDefsQuery = useCargoDefinitions(organisationId);
  const sitesQuery = useSitesWithMembers(organisationId);

  // Memoised so the `?? []` fallback doesn't hand every downstream memo a fresh
  // array — and a fresh dependency — on each render.
  const orders = useMemo(() => ordersQuery.data ?? [], [ordersQuery.data]);

  /*
   * Tasks are one request per order, so only the orders Plan can show are
   * fetched: Open and In progress, plus Completed when the switch is on. Draft,
   * Cancelled (and Completed by default) are never listed here.
   *
   * The catch is a LOAD: its lines name cargo items, not orders, and the only
   * way to find a line's order and stop is that order's tasks. A live load can
   * carry a hidden order's cargo (a Draft order manifested, or one order of a
   * mixed load already completed). So once the shown orders' tasks are in, a
   * visible load with a line none of them explains pulls in the hidden orders'
   * tasks too — rare, and it keeps every load whole.
   */
  const shownOrder = (status: string) =>
    status === 'Open' || status === 'InProgress' || (showCompleted && status === 'Completed');
  const loads = useMemo(() => loadsQuery.data ?? [], [loadsQuery.data]);
  const liveLoads = useMemo(
    () =>
      loads.filter(
        (l) =>
          l.status !== 'Draft' &&
          l.status !== 'Cancelled' &&
          (showCompleted || l.status !== 'Completed'),
      ),
    [loads, showCompleted],
  );
  const shownQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: orderTaskKeys.byOrder(o.id),
      queryFn: () => tmsApi.getOrderTasks(o.id),
      enabled: !!organisationId && shownOrder(o.status),
      staleTime: 30 * 1000,
    })),
  });
  const shownTasksReady = orders.every((o, i) => !shownOrder(o.status) || !shownQueries[i]?.isPending);
  const needHiddenOrders = useMemo(() => {
    if (!shownTasksReady || liveLoads.length === 0) return false;
    const known = new Set<string>();
    orders.forEach((o, i) => {
      if (!shownOrder(o.status)) return;
      for (const t of shownQueries[i]?.data ?? []) for (const it of t.cargoItems ?? []) known.add(it.id);
    });
    return liveLoads.some((l) => l.lines.some((line) => !known.has(line.orderTaskCargoItemId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownTasksReady, liveLoads, orders, shownQueries.map((q) => q.dataUpdatedAt).join(','), showCompleted]);
  // Same keys as above, so a hidden order's tasks are fetched once and shared.
  const taskQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: orderTaskKeys.byOrder(o.id),
      queryFn: () => tmsApi.getOrderTasks(o.id),
      enabled: !!organisationId && (shownOrder(o.status) || needHiddenOrders),
      staleTime: 30 * 1000,
    })),
  });

  // CO2 too: asking for an order with no estimate yet makes the server work
  // one out, so only for the orders whose tasks are being read.
  const orderIds = useMemo(
    () => orders.filter((o) => shownOrder(o.status) || needHiddenOrders).map((o) => o.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, needHiddenOrders, showCompleted],
  );
  const emissionsByOrderId = useOrdersEmissions(organisationId, orderIds);

  const customersById = useMemo(() => {
    const m = new Map<string, Party>();
    for (const p of customersQuery.data ?? []) m.set(p.id, p);
    return m;
  }, [customersQuery.data]);

  const addressById = useMemo(() => {
    const m = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) m.set(a.id, a);
    return m;
  }, [locationsQuery.data]);

  const serviceTagsById = useMemo(() => {
    const m = new Map<string, ServiceTag>();
    for (const t of serviceTagsQuery.data ?? []) m.set(t.id, t);
    return m;
  }, [serviceTagsQuery.data]);

  const cargoDefsById = useMemo(() => {
    const m = new Map<string, CargoDefinition>();
    for (const d of cargoDefsQuery.data ?? []) m.set(d.id, d);
    return m;
  }, [cargoDefsQuery.data]);

  const siteCountryById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sitesQuery.data ?? []) {
      const country = s.addressId ? addressById.get(s.addressId)?.countryIsoCode : undefined;
      if (country) m.set(s.id, country);
    }
    return m;
  }, [sitesQuery.data, addressById]);

  // taskQueries is a fresh array each render; key the memo on its data so it
  // recomputes when any order's tasks resolve, not on identity.
  const tasksSignature = taskQueries.map((q) => q.dataUpdatedAt).join(',');
  const todayYmd = new Date().toISOString().slice(0, 10);
  const movements = useMemo(() => {
    const tasksByOrderId = new Map<string, OrderTask[]>();
    orders.forEach((o, i) => tasksByOrderId.set(o.id, taskQueries[i]?.data ?? []));
    return buildMovements({
      orders,
      tasksByOrderId,
      // Only the loads Plan shows. An order on a hidden (Draft) load is still
      // work to plan, so it must come through as its own row, not vanish
      // into a load nobody can see.
      loads: liveLoads,
      customersById,
      addressById,
      serviceTagsById,
      cargoDefsById,
      siteCountryById,
      assignments: assignmentsQuery.data ?? [],
      todayYmd,
      emissionsByOrderId,
    }).filter((m) => isOnPlan(m, showCompleted));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orders,
    tasksSignature,
    liveLoads,
    customersById,
    addressById,
    serviceTagsById,
    cargoDefsById,
    siteCountryById,
    assignmentsQuery.data,
    todayYmd,
    emissionsByOrderId,
    showCompleted,
  ]);

  // Cargo options come from the org's catalogue (so the list is stable); direction
  // options are derived from the rows on screen, since a country pair only exists
  // if some job actually flows that way.
  const cargoOpts = useMemo(() => cargoTypeOptions(cargoDefsQuery.data ?? []), [cargoDefsQuery.data]);
  const directionOpts = useMemo(() => directionOptions(movements), [movements]);

  const isLoading =
    ordersQuery.isLoading ||
    loadsQuery.isLoading ||
    customersQuery.isLoading ||
    locationsQuery.isLoading ||
    assignmentsQuery.isLoading ||
    taskQueries.some((q) => q.isLoading);
  // Assignments are deliberately absent from the error gate: losing them costs
  // the crew columns, not the queue. Blocking the whole board over it would be a
  // worse outcome than rows that read "Unassigned".
  const isError =
    ordersQuery.isError || loadsQuery.isError || customersQuery.isError || locationsQuery.isError;

  const orderNumberById = useMemo(
    () => new Map(orders.map((o) => [o.id, o.orderNumber])),
    [orders],
  );

  return {
    movements,
    assignments: assignmentsQuery.data ?? [],
    orderNumberById,
    depotCountry: depotSiteId ? (siteCountryById.get(depotSiteId) ?? null) : null,
    cargoOptions: cargoOpts,
    directionOptions: directionOpts,
    isLoading,
    isError,
  };
}
